import { useState } from 'react';
import { X, HelpCircle, Upload, FileText, TrendingUp, Link2, SlidersHorizontal, CheckCircle } from 'lucide-react';
import { T } from '../theme';
import { BIZ_CLASS } from '../utils/constants';

const TABS = [
  { id: 'start',   label: 'Getting Started' },
  { id: 'classes', label: 'Classifications' },
  { id: 'linking', label: 'Linking Receipts' },
  { id: 'filters', label: 'Filters & Views' },
];

function Step({ num, title, children }) {
  return (
    <div style={{ display: 'flex', gap: 14, marginBottom: 20 }}>
      <div style={{
        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
        background: T.gradTeal, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14, fontWeight: 900,
      }}>{num}</div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#111', marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.6 }}>{children}</div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, color = T.tealDeep, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Icon size={16} color={color} />
        <span style={{ fontSize: 13, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function GettingStarted({ onImportCreditCard, onImportBank, onGoToExpenses, onClose }) {
  return (
    <div>
      <p style={{ fontSize: 14, color: '#4B5563', lineHeight: 1.65, marginBottom: 20 }}>
        My Finances is your real-time P&amp;L and transaction dashboard. It pulls together all your expense reports,
        credit card statements, and bank accounts so you can classify every dollar as business or personal — and see your net instantly.
      </p>

      <Section icon={Upload} title="Step 1 — Add your data">
        <Step num="1" title="Import a bank or credit card statement">
          Download a CSV from your bank's website, then tap <strong>Load → Import Bank Statement</strong> or{' '}
          <strong>Import Credit Card</strong>. Every charge appears in the list immediately.
          {(onImportCreditCard || onImportBank) && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              {onImportCreditCard && (
                <button
                  onClick={() => { onClose(); onImportCreditCard(); }}
                  style={{ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 8, border: `1.5px solid ${T.tealDeep}`, background: T.tealPale, color: T.tealDeep, cursor: 'pointer' }}
                >
                  Import Credit Card CSV
                </button>
              )}
              {onImportBank && (
                <button
                  onClick={() => { onClose(); onImportBank(); }}
                  style={{ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 8, border: '1.5px solid #0369a1', background: '#EFF6FF', color: '#0369a1', cursor: 'pointer' }}
                >
                  Import Bank Statement CSV
                </button>
              )}
            </div>
          )}
        </Step>
        <Step num="2" title="Or scan receipts into expense reports">
          Use the <strong>Add</strong> or <strong>Capture</strong> tab to scan receipts and build expense reports.
          Those show up here as "Expense Reports" alongside your bank data.
          {onGoToExpenses && (
            <div style={{ marginTop: 8 }}>
              <button
                onClick={() => { onClose(); onGoToExpenses(); }}
                style={{ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 8, border: '1.5px solid #6B7280', background: '#F9FAFB', color: '#374151', cursor: 'pointer' }}
              >
                Go to Expense Reports
              </button>
            </div>
          )}
        </Step>
      </Section>

      <Section icon={FileText} title="Step 2 — Classify each transaction">
        <Step num="3" title="Tap a transaction to expand it">
          Each row shows the vendor, date, and amount. Tap it to reveal the{' '}
          <strong>Classify</strong> dropdown. Pick the right classification — Business Expense,
          Personal, Transfer, etc. — and it's saved instantly.
        </Step>
        <Step num="4" title="Use bulk reclassification to go faster">
          After you classify one transaction from a vendor (e.g. "Uber"), a banner appears
          offering to apply the same classification to all other transactions from that vendor.
          One tap to accept.
        </Step>
      </Section>

      <Section icon={TrendingUp} title="Step 3 — Read your P&L">
        <Step num="5" title="Watch the numbers update in real time">
          The <strong>Biz Income</strong>, <strong>Biz Expenses</strong>, and <strong>Net</strong>{' '}
          cards at the top update as you classify. Use the date range picker to focus on a
          specific month, quarter, or year. Use <strong>Group → By month</strong> to see trends over time.
        </Step>
      </Section>

      <div style={{ background: T.tealPale, borderRadius: 10, padding: '12px 14px', fontSize: 13, color: T.tealDeep, lineHeight: 1.6 }}>
        <strong>Tip:</strong> Supported banks for CSV import: Chase, Bank of America, Wells Fargo (more coming).
        Most credit card issuers export a standard CSV — try uploading yours even if it's not listed.
      </div>
    </div>
  );
}

function ClassificationsHelp() {
  const DESCRIPTIONS = {
    business_expense:    'Deductible business spending — rent, supplies, software, travel, meals with clients, etc.',
    business_income:     'Revenue you earned through your business — invoices, payments, consulting fees.',
    personal_income:     'Non-business income — salary from an employer, dividends, personal transfers in.',
    personal:            'Personal spending that has no business deduction — groceries, personal subscriptions, etc.',
    transfer:            'Money moved between your own accounts. Not income or expense — mark these to zero them out.',
    unclassified_credit: 'A credit or refund you haven\'t reviewed yet. Classify it as income, personal income, or a transfer.',
    unclassified_debit:  'An expense you haven\'t reviewed yet. Most new imports start here.',
  };

  return (
    <div>
      <p style={{ fontSize: 14, color: '#4B5563', lineHeight: 1.65, marginBottom: 16 }}>
        Every transaction gets one classification. The P&amp;L only counts <strong>Business Expense</strong> and{' '}
        <strong>Business Income</strong> — everything else is tracked but excluded from the net calculation.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {Object.entries(BIZ_CLASS).map(([key, c]) => (
          <div key={key} style={{
            display: 'flex', gap: 12, alignItems: 'flex-start',
            background: c.bg, border: `1.5px solid ${c.color}22`,
            borderRadius: 10, padding: '10px 12px',
          }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: c.color, flexShrink: 0, marginTop: 3 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: c.color, marginBottom: 2 }}>{c.label}</div>
              <div style={{ fontSize: 12, color: '#4B5563', lineHeight: 1.5 }}>{DESCRIPTIONS[key]}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LinkingHelp() {
  return (
    <div>
      <p style={{ fontSize: 14, color: '#4B5563', lineHeight: 1.65, marginBottom: 16 }}>
        If you both scan a receipt <em>and</em> import the same charge from a bank or CC statement,
        you'd have two entries for the same dollar. Linking merges them into one — no double-counting.
      </p>

      <Section icon={Link2} title="Manual linking">
        <div style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.65, marginBottom: 12 }}>
          1. Tap <strong>Link</strong> in the controls bar to enter link mode.<br />
          2. Tap one transaction from an <em>expense report</em> (🧾) and one from a <em>CC or bank account</em> (💳 / 🏦).<br />
          3. Tap <strong>Link these</strong>. They merge into a single row showing both sources.
        </div>
      </Section>

      <Section icon={CheckCircle} title="AI reconcile tool" color="#7C3AED">
        <div style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.65, marginBottom: 12 }}>
          Tap <strong>Link Tool</strong> for the AI-assisted reconcile view. It scores potential matches
          by amount, date, and vendor — showing the best candidates at the top so you can confirm
          or skip in one tap.
        </div>
      </Section>

      <div style={{ background: '#FEF3C7', borderRadius: 10, padding: '12px 14px', fontSize: 13, color: '#92400E', lineHeight: 1.6 }}>
        <strong>Note:</strong> Linked pairs are saved permanently to both source files.
        To unlink, expand the transaction and tap <strong>Unlink</strong>.
      </div>
    </div>
  );
}

function FiltersHelp() {
  return (
    <div>
      <Section icon={SlidersHorizontal} title="Classification filter">
        <div style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.65, marginBottom: 12 }}>
          Tap any classification row in the <strong>Classifications</strong> list to show or hide
          transactions of that type. Use <strong>Select All</strong> to reset, or{' '}
          <strong>Select None</strong> to start with a blank slate and add only what you need.
        </div>
      </Section>

      <Section icon={FileText} title="Expense Reports & Transaction Accounts">
        <div style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.65, marginBottom: 12 }}>
          Use the <strong>Expense Reports</strong> and <strong>Transaction Accounts</strong> lists
          to focus on specific reports or bank accounts. Multiple CSV imports from the same bank
          account are grouped under one account name automatically.
        </div>
      </Section>

      <Section icon={TrendingUp} title="Date range">
        <div style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.65, marginBottom: 12 }}>
          The date picker at the top filters all transactions, the P&amp;L cards, and the
          raw net total. Use presets like <strong>This Month</strong> or <strong>This Year</strong>,
          or pick a custom range. The P&amp;L always reflects only the visible date range.
        </div>
      </Section>

      <Section icon={FileText} title="Grouping & sorting">
        <div style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.65 }}>
          Use <strong>Group by</strong> to organise transactions by month, classification, category,
          or report. Combine with <strong>Collapse all</strong> for a high-level overview, then
          expand individual groups to drill down.
        </div>
      </Section>
    </div>
  );
}

export default function BSVHelpModal({ onClose, onImportCreditCard, onImportBank, onGoToExpenses, initialTab = 'start' }) {
  const [activeTab, setActiveTab] = useState(initialTab);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 8,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 20,
          width: '100%', maxWidth: 560,
          height: '92vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <HelpCircle size={18} color={T.tealDeep} />
            <span style={{ fontSize: 17, fontWeight: 900, color: '#111' }}>My Finances — Help</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', display: 'flex', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, padding: '12px 20px 0', borderBottom: '1px solid #E5E7EB', overflowX: 'auto', alignItems: 'flex-end' }}>
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                padding: '7px 12px 6px', borderRadius: '8px 8px 0 0', border: 'none',
                fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                background: 'transparent',
                color: activeTab === t.id ? T.tealDeep : '#6B7280',
                borderBottom: activeTab === t.id ? `2px solid ${T.tealDeep}` : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', padding: '20px 20px 24px', flex: 1 }}>
          {activeTab === 'start'   && <GettingStarted onImportCreditCard={onImportCreditCard} onImportBank={onImportBank} onGoToExpenses={onGoToExpenses} onClose={onClose} />}
          {activeTab === 'classes' && <ClassificationsHelp />}
          {activeTab === 'linking' && <LinkingHelp />}
          {activeTab === 'filters' && <FiltersHelp />}
        </div>
      </div>
    </div>
  );
}
